import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AppLanguage, formatMessage, getInitialLanguage, LANGUAGE_STORAGE_KEY, MessageKey, messages } from '../i18n';
import { installAdminDomTranslator, translateAdminDom } from '../utils/domTranslator';

interface LanguageContextValue {
  language: AppLanguage;
  setLanguage: (language: AppLanguage) => void;
  t: (key: MessageKey, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<AppLanguage>(getInitialLanguage);
  const languageRef = useRef<AppLanguage>(language);

  const setLanguage = (nextLanguage: AppLanguage) => {
    setLanguageState(nextLanguage);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, nextLanguage);
  };

  useEffect(() => {
    languageRef.current = language;
    document.title = messages[language].appName;
    installAdminDomTranslator(() => languageRef.current);
    // 等 React 提交完再跑 DOM 翻译，避免波兰语文案被缓存后切不回中文
    const frameId = requestAnimationFrame(() => {
      translateAdminDom(language);
    });
    return () => cancelAnimationFrame(frameId);
  }, [language]);

  const value = useMemo<LanguageContextValue>(() => ({
    language,
    setLanguage,
    t: (key, params) => formatMessage(language, key, params),
  }), [language]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used inside LanguageProvider');
  }
  return context;
};
